import { AutocompleteAPI, FetchAPI, FieldAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnCancel, OnChange, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import BaseController from '../BaseController';
import { Inject } from '@odinjs/odin';
import HealthInsuranceCategory from '../../interfaces/health-insurance-category';
import HealthInsurancePlan from '../../interfaces/health-insurance-plan';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import UserContext from '../../utils/user-context';
import { ContextType } from '@philips-internal/oswald-ui-core';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { VisibilityFieldsResponse } from '../pseudonym-appointments/pseudonym-interfaces';
import { InsuranceFields } from '../../utils/insurance-fields';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { INSURANCE_CATEGORY_VISIBILITY, INSURANCE_VISIBILITY } from '../../constants/field-visibility';
import { FragmentNames, InsuranceAutocompleteModalAliases } from '../../codegen';
import HealthInsurance from '../../interfaces/health-insurance';

@Controller({ fragment: FragmentNames.INSURANCE_AUTOCOMPLETE_MODAL })
export default class InsuranceAutocompleteModalController extends BaseController {

  @Inject(InsuranceAutocompleteModalAliases.FIELD_PLAN)
    fieldPlan!: FieldAPI

  @Inject(InsuranceAutocompleteModalAliases.FIELD_CATEGORY)
    fieldCategory!: FieldAPI;

  @Inject(InsuranceAutocompleteModalAliases.FIELD_CHECKBOX_SAVE_INSURANCE)
    fieldCheckboxSaveInsurace!: FieldAPI;

  @Inject(InsuranceAutocompleteModalAliases.FETCH_CATEGORY)
    categoryFetch!: FetchAPI;

  @Inject(InsuranceAutocompleteModalAliases.FETCH_PLAN)
    planFetch!: FetchAPI;

  @Inject(InsuranceAutocompleteModalAliases.AUTOCOMPLETE_CATEGORY)
    autoCompleteInsuranceCategory!: AutocompleteAPI;

  @Inject(InsuranceAutocompleteModalAliases.AUTOCOMPLETE_PLAN)
    autoCompleteInsurancePlan!: AutocompleteAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject(InsuranceAutocompleteModalAliases.FIELD_HEALTH_CARD_NUMBER)
    fieldHealthCardNumber!: FieldAPI;

  @OnMount({target: InsuranceAutocompleteModalAliases.LAYOUT_INSURANCES})
  handleLoadModal(): void {
    const patientId = Number(UserContext.patientId);
    if(patientId === 0) {
      this.fieldCheckboxSaveInsurace.setVisible(false);
    }
  }

  @OnChange({ target: InsuranceAutocompleteModalAliases.AUTOCOMPLETE_HEALTH })
  handleChangeInsurance(event: ChangeEvent<HealthInsurance>): void {
    const autocomplete = event.target as unknown as AutocompleteAPI;
    this.clearAutocompletesOnChange(autocomplete);
    if (event?.value?.cdConvenio) {
      this.context.set({ cdConvenio: event.value.cdConvenio, ieInsurancePrivate: event.value.ieInsurancePrivate });
      if (this.fieldCategory.getVisible()) {
        this.autoCompleteInsuranceCategory.setEnabled(true);
        this.showInsuranceCategory(event.value.cdConvenio, '');
      }
      this.autoCompleteInsurancePlan.setEnabled(true);
      this.showInsurancePlan(event.value.cdConvenio, '');
    } else {
      if (this.fieldCategory.getVisible()) {
        this.autoCompleteInsuranceCategory.setEnabled(false);
      }
      this.autoCompleteInsurancePlan.setEnabled(false);
    }
  }

  @OnChange({ target: InsuranceAutocompleteModalAliases.AUTOCOMPLETE_CATEGORY })
  onChangeCategory(event: ChangeEvent<HealthInsuranceCategory>): void {
    if (event?.value) {
      this.showInsurancePlan(event.value.cdConvenio, event.value.cdCategoria);
    } else if (event?.oldValue) {
      this.showInsurancePlan(event.oldValue.cdConvenio, '');
    }
  }

  @OnChange({ target: InsuranceAutocompleteModalAliases.AUTOCOMPLETE_PLAN })
  onChangePlan(event: ChangeEvent<HealthInsurancePlan>): void {
    if (!this.fieldCategory.getVisible()) return;
    if (event?.value) {
      this.showInsuranceCategory(event.value.cdConvenio, event.value.cdPlano);
    } else if (event?.oldValue) {
      this.showInsuranceCategory(event.oldValue.cdConvenio, '');
    }
  }

  clearAutocompletesOnChange(autocomplete: AutocompleteAPI): void {
    switch (autocomplete.getAlias()) {
      case InsuranceAutocompleteModalAliases.AUTOCOMPLETE_HEALTH:
        this.fieldPlan.setValue({});
        this.fieldCategory.setValue({});
        if (this.fieldCategory.getVisible()) {
          this.autoCompleteInsuranceCategory.setValue({});
        }
        this.autoCompleteInsurancePlan.setValue({});
        break;
      default:
        break;
    }
  }

  showInsuranceCategory(cdConvenio: string, planCode: string): void {
    setTimeout(() => {
      this.categoryFetch.setParameters({ cdConvenio, planCode });
      this.categoryFetch.forceFetch();
    });
  }

  showInsurancePlan(cdConvenio: string, categoryCode: string): void {
    if (cdConvenio) {
      this.autoCompleteInsurancePlan.setEnabled(true);
      setTimeout(() => {
        const validateRules = true;
        this.planFetch.setParameters({ categoryCode, cdConvenio, validateRules});
        this.planFetch.forceFetch();
      });
    }
  }

  @OnSubmit({target: InsuranceAutocompleteModalAliases.FORM_INSURANCE})
  handleSubmitFormInsurnace(event: SubmitEvent): void {
    if (event) {
      this.submittableFragmentContext.submit(new SubmitEvent({ record: event.record }));
    }
  }

  @OnCancel({target: InsuranceAutocompleteModalAliases.FORM_INSURANCE})
  handleCancelFormInsurance(): void {
    this.submittableFragmentContext.submit(new SubmitEvent());
  }

  @OnMount({target: InsuranceAutocompleteModalAliases.FORM_INSURANCE})
  handleMountFormInsurance(): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_VISIBILITY_OF_FIELDS).then((response) => {
      const mandatoryFields = response.body as VisibilityFieldsResponse;

      this.fieldCategory.setVisible(mandatoryFields.insuranceCategory !== INSURANCE_CATEGORY_VISIBILITY.NOT_VISIBLE);
      if (this.fieldCategory.isVisible() && mandatoryFields.insuranceCategory === INSURANCE_CATEGORY_VISIBILITY.MANDATORY) {
        this.fieldCategory.setValidators([InsuranceFields.required]);
      }

      if (mandatoryFields.insurancePlan === INSURANCE_VISIBILITY.MANDATORY) {
        this.fieldPlan.setValidators([InsuranceFields.required]);
      }
      if (mandatoryFields.insuranceCardNumber === INSURANCE_VISIBILITY.MANDATORY) {
        this.fieldHealthCardNumber.setValidators([InsuranceFields.required]);
      }
    });
  }

}